import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { StatTiles } from '../../components/shell/Page';
import RecordPage from '../../components/shell/RecordPage';
import PasswordField from '../../components/shell/PasswordField';
import DataGrid, { StatusBadge } from '../../components/datagrid/DataGrid';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Buildings,
  Users,
  Books,
  Plus,
  ChartLineUp,
  UserPlus,
  Eye,
  PencilSimple,
  Trash,
  ToggleRight,
  ArrowSquareOut
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Globe } from 'lucide-react';
import CustomDomainDialog from '../../components/CustomDomainDialog';
import { paneForm, PaneFooter } from '../../components/shell/SidePane';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { getPlatformDomains, tenantPortalUrl, tenantPortalLabel, tenantSubdomainHost } from '../../lib/tenantDetection';

export default function SuperAdminDashboard() {
  const { api } = useAuth();
  const [stats, setStats] = useState({});
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [domainDialogOpen, setDomainDialogOpen] = useState(false);
  const [domainTenant, setDomainTenant] = useState(null);
  const [assignAdminDialogOpen, setAssignAdminDialogOpen] = useState(false);
  const [viewAdminsDialogOpen, setViewAdminsDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantAdmins, setTenantAdmins] = useState([]);
  const [platform, setPlatform] = useState({ base_domains: ['mylms.in'], default_base_domain: 'mylms.in' });
  const [newTenant, setNewTenant] = useState({ name: '', subdomain: '', base_domain: '' });
  const [editTenant, setEditTenant] = useState({ name: '', subdomain: '', base_domain: '', is_active: true });
  const [adminForm, setAdminForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: ''
  });
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    getPlatformDomains({ force: true }).then((p) => {
      setPlatform(p);
      setNewTenant((t) => ({ ...t, base_domain: t.base_domain || p.default_base_domain }));
    });
    try {
      const [statsRes, tenantsRes] = await Promise.all([
        api.get('/reports/dashboard'),
        api.get('/tenants')
      ]);
      setStats(statsRes.data);
      setTenants(tenantsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/tenants', newTenant);
      toast.success('Tenant created successfully');
      setDialogOpen(false);
      setNewTenant({ name: '', subdomain: '', base_domain: platform.default_base_domain });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tenant');
    } finally {
      setCreating(false);
    }
  };

  const handleEditTenant = (tenant) => {
    setSelectedTenant(tenant);
    closePane();
    setEditTenant({
      name: tenant.name,
      subdomain: tenant.subdomain,
      base_domain: tenant.base_domain || platform.default_base_domain,
      is_active: tenant.is_active !== false
    });
    setEditDialogOpen(true);
  };

  const handleUpdateTenant = async (e) => {
    e.preventDefault();
    if (!selectedTenant) return;
    setSubmitting(true);
    try {
      await api.put(`/tenants/${selectedTenant.id}`, editTenant);
      toast.success('Tenant updated successfully');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tenant');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTenant = async (tenant) => {
    if (!confirm(`Are you sure you want to delete "${tenant.name}"? This action cannot be undone and will remove all associated data.`)) {
      return;
    }
    try {
      await api.delete(`/tenants/${tenant.id}`);
      toast.success('Tenant deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tenant');
    }
  };

  const handleToggleTenantStatus = async (tenant) => {
    try {
      await api.put(`/tenants/${tenant.id}`, { is_active: !tenant.is_active });
      toast.success(`Tenant ${tenant.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tenant status');
    }
  };

  const handleAssignAdmin = async (e) => {
    e.preventDefault();
    if (!selectedTenant) return;
    setSubmitting(true);
    try {
      await api.post(`/tenants/${selectedTenant.id}/assign-admin`, adminForm);
      toast.success('Tenant Admin assigned successfully');
      setAssignAdminDialogOpen(false);
      setAdminForm({ email: '', password: '', first_name: '', last_name: '' });
      setSelectedTenant(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign admin');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewAdmins = async (tenant) => {
    setSelectedTenant(tenant);
    try {
      const res = await api.get(`/tenants/${tenant.id}/admins`);
      setTenantAdmins(res.data);
    } catch (error) {
      setTenantAdmins([]);
    }
    closePane();
    setViewAdminsDialogOpen(true);
  };

  const openDomainDialog = (tenant) => {
    setDomainTenant(tenant);
    setDomainDialogOpen(true);
  };

  // ─── Side pane (small CRUD forms open here instead of popups) ───
  const closePane = () => { setDialogOpen(false); setEditDialogOpen(false); setAssignAdminDialogOpen(false); setViewAdminsDialogOpen(false); };
  const openPane = (which) => { closePane(); which(true); };
  let side = null;
  if (dialogOpen) {
    const f = paneForm({ id: 'create-tenant-form', onSubmit: handleCreateTenant, onCancel: closePane, submitLabel: 'Create tenant', submitting: creating, submitIcon: Plus, children: (
      <>
      <div className="field">
        <Label htmlFor="name">Organization Name</Label>
        <Input
          id="name"
          value={newTenant.name}
          onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })}
          placeholder="Acme Corporation"
          required
          data-testid="tenant-name-input"
        />
      </div>
      <div className="field">
        <Label htmlFor="subdomain">Subdomain</Label>
        <div className="flex items-center gap-2">
          <Input
            id="subdomain"
            value={newTenant.subdomain}
            onChange={(e) => setNewTenant({ ...newTenant, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
            placeholder="acme"
            required
            className="flex-1"
            data-testid="tenant-subdomain-input"
          />
          <span className="text-muted-foreground text-sm">.</span>
          <Select value={newTenant.base_domain || platform.default_base_domain} onValueChange={(v) => setNewTenant({ ...newTenant, base_domain: v })}>
            <SelectTrigger className="w-[200px] font-mono text-xs" data-testid="tenant-base-domain-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {platform.base_domains.map((d) => (
                <SelectItem key={d} value={d} className="font-mono text-xs">{d}{d === platform.default_base_domain ? '  (default)' : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="field-hint">
          Portal: <span className="font-mono">https://{newTenant.subdomain || 'acme'}.{newTenant.base_domain || platform.default_base_domain}</span>
          {' · '}manage the list under Administration → Platform domains
        </p>
      </div>
      </>
    ) });
    side = { open: true, onOpenChange: closePane, title: 'New tenant', subtitle: 'Create a new organization on the platform', icon: Buildings, children: f.body, footer: f.footer, testid: 'create-tenant-pane' };
  } else if (editDialogOpen) {
    const f = paneForm({ id: 'edit-tenant-form', onSubmit: handleUpdateTenant, onCancel: closePane, submitLabel: 'Save changes', submitting, children: (
      <>
      <div className="field">
        <Label>Organization Name</Label>
        <Input
          value={editTenant.name}
          onChange={(e) => setEditTenant({ ...editTenant, name: e.target.value })}
          required
        />
      </div>
      <div className="field">
        <Label>Subdomain</Label>
        <div className="flex items-center gap-2">
          <Input
            value={editTenant.subdomain}
            onChange={(e) => setEditTenant({ ...editTenant, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
            required
            className="flex-1"
          />
          <span className="text-muted-foreground text-sm">.</span>
          <Select value={editTenant.base_domain || platform.default_base_domain} onValueChange={(v) => setEditTenant({ ...editTenant, base_domain: v })}>
            <SelectTrigger className="w-[200px] font-mono text-xs" data-testid="edit-base-domain-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {platform.base_domains.map((d) => (
                <SelectItem key={d} value={d} className="font-mono text-xs">{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="field-hint">The portal stays at <span className="font-mono">/tenant/{editTenant.subdomain}</span> on any platform host. The base domain only sets the optional subdomain address <span className="font-mono">{editTenant.subdomain}.{editTenant.base_domain || platform.default_base_domain}</span>, which needs wildcard DNS for that domain.</p>
      </div>
      <div className="flex items-center justify-between">
        <Label>Active Status</Label>
        <Switch
          checked={editTenant.is_active}
          onCheckedChange={(checked) => setEditTenant({ ...editTenant, is_active: checked })}
        />
      </div>
      </>
    ) });
    side = { open: true, onOpenChange: closePane, title: `Edit: ${selectedTenant?.name || 'tenant'}`, icon: PencilSimple, children: f.body, footer: f.footer, testid: 'edit-tenant-pane' };
  } else if (assignAdminDialogOpen) {
    const f = paneForm({ id: 'assign-admin-form', onSubmit: handleAssignAdmin, onCancel: closePane, submitLabel: 'Add admin', submitting, submitIcon: UserPlus, children: (
      <>
      <div className="grid grid-cols-2 gap-4">
        <div className="field">
          <Label>First Name</Label>
          <Input
            value={adminForm.first_name}
            onChange={(e) => setAdminForm({...adminForm, first_name: e.target.value})}
            required
          />
        </div>
        <div className="field">
          <Label>Last Name</Label>
          <Input
            value={adminForm.last_name}
            onChange={(e) => setAdminForm({...adminForm, last_name: e.target.value})}
            required
          />
        </div>
      </div>
      <div className="field">
        <Label>Email</Label>
        <Input
          type="email"
          value={adminForm.email}
          onChange={(e) => setAdminForm({...adminForm, email: e.target.value})}
          required
        />
      </div>
      <PasswordField
        label="Password"
        value={adminForm.password}
        onChange={(v) => setAdminForm({...adminForm, password: v})}
        required
        generate
        placeholder="At least 6 characters"
        testid="admin-password-input"
      />
      </>
    ) });
    side = { open: true, onOpenChange: closePane, title: 'Assign tenant admin', subtitle: selectedTenant?.name, icon: UserPlus, children: f.body, footer: f.footer, testid: 'assign-admin-pane' };
  } else if (viewAdminsDialogOpen) {
    side = {
      open: true, onOpenChange: closePane, title: 'Tenant administrators', subtitle: selectedTenant?.name, icon: Users, testid: 'view-admins-pane',
      children: (
    <div className="space-y-3">
      {tenantAdmins.length === 0 ? (
        <p className="text-muted-foreground text-center py-4">No administrators assigned yet</p>
      ) : (
        tenantAdmins.map((admin) => (
          <div key={admin.id} className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <div className="font-medium">{admin.first_name} {admin.last_name}</div>
              <div className="text-sm text-muted-foreground">{admin.email}</div>
            </div>
            <Badge variant={admin.is_active ? 'default' : 'secondary'}>
              {admin.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        ))
      )}
    </div>
      ),
      footer: (
        <PaneFooter onCancel={closePane} cancelLabel="Close" onSubmit={() => openPane(setAssignAdminDialogOpen)} submitLabel="Add another admin" submitIcon={UserPlus} />
      ),
    };
  }

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="super-admin-dashboard"
        title="Dashboard"
        subtitle="Platform overview"
        onBack={null}
        findable={false}
        onRefresh={fetchData}
        refreshing={loading}
        loading={loading}
        maxWidth="none"
        side={side}
        primary={[
          { key: 'new-tenant', label: 'New tenant', icon: Plus, onClick: () => openPane(setDialogOpen), testid: 'create-tenant-btn' },
        ]}
      >
        <StatTiles items={[
          { label: 'Total tenants', value: stats.total_tenants || 0, icon: Buildings },
          { label: 'Active tenants', value: stats.active_tenants || 0, icon: ChartLineUp, iconClass: 'bg-emerald-100 text-emerald-700' },
          { label: 'Total users', value: stats.total_users || 0, icon: Users, iconClass: 'bg-sky-100 text-sky-700' },
          { label: 'Total courses', value: stats.total_courses || 0, icon: Books, iconClass: 'bg-violet-100 text-violet-700' },
        ]} />

        <DataGrid
          title="Tenants"
          rows={tenants}
          loading={loading}
          onRefresh={fetchData}
          searchKeys={['custom_domain']}
          columns={[
            {
              key: 'name', header: 'Name',
              render: (t) => (
                <span className="inline-flex items-center gap-1.5 font-medium" data-testid={`tenant-row-${t.subdomain}`}>
                  {t.name}
                  <a
                    href={tenantPortalUrl(t)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary"
                    title={`Open ${t.name} portal`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ArrowSquareOut className="w-3.5 h-3.5" />
                  </a>
                </span>
              ),
            },
            {
              key: 'host', header: 'Tenant URL', accessor: (t) => tenantPortalLabel(t), className: 'font-mono text-xs',
              render: (t) => (
                <span title={`Subdomain address (needs wildcard DNS): ${tenantSubdomainHost(t, platform.default_base_domain)}`}>
                  {tenantPortalLabel(t)}
                </span>
              ),
            },
            { key: 'subdomain', header: 'Subdomain', className: 'font-mono text-xs', hidden: true },
            {
              key: 'custom_domain', header: 'Custom domain', accessor: (t) => t.custom_domain || '',
              render: (t) => (
                <span className="inline-flex items-center gap-2" data-testid={`manage-domain-${t.subdomain}`}>
                  {t.custom_domain ? (
                    <>
                      <span className="font-mono text-xs">{t.custom_domain}</span>
                      {t.custom_domain_meta?.status && <StatusBadge value={t.custom_domain_meta.status} />}
                    </>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </span>
              ),
            },
            { key: 'user_count', header: 'Users', width: 90, align: 'right', accessor: (t) => t.user_count || 0 },
            { key: 'course_count', header: 'Courses', width: 90, align: 'right', accessor: (t) => t.course_count || 0 },
            { key: 'is_active', header: 'Status', width: 110, accessor: (t) => t.is_active !== false, render: (t) => <StatusBadge value={t.is_active !== false} /> },
            { key: 'created_at', header: 'Created', width: 120, render: (t) => new Date(t.created_at).toLocaleDateString(), className: 'text-muted-foreground' },
          ]}
          rowTone={(t) => (t.is_active === false ? 'muted' : null)}
          onRowDoubleClick={openDomainDialog}
          selectionActions={[
            { key: 'assign-admin', label: 'Assign admin', icon: UserPlus, onClick: ([t]) => { setSelectedTenant(t); openPane(setAssignAdminDialogOpen); } },
            { key: 'view-admins', label: 'View admins', icon: Eye, onClick: ([t]) => handleViewAdmins(t) },
            { key: 'edit', label: 'Edit', icon: PencilSimple, onClick: ([t]) => handleEditTenant(t) },
            { key: 'domain', label: 'Custom domain', icon: Globe, onClick: ([t]) => openDomainDialog(t) },
            { key: 'toggle', label: 'Activate / Deactivate', icon: ToggleRight, onClick: ([t]) => handleToggleTenantStatus(t) },
            { key: 'delete', label: 'Delete', icon: Trash, variant: 'destructive', onClick: ([t]) => handleDeleteTenant(t) },
          ]}
          emptyMessage="No tenants yet. Create your first tenant to get started."
        />





        {/* Custom Domain Dialog */}
        <CustomDomainDialog
          open={domainDialogOpen}
          onOpenChange={setDomainDialogOpen}
          tenant={domainTenant}
          onChange={async () => {
            // Refetch the tenant so meta refreshes
            try {
              const res = await api.get('/tenants');
              setTenants(res.data || []);
              if (domainTenant) {
                const fresh = (res.data || []).find(t => t.id === domainTenant.id);
                if (fresh) setDomainTenant(fresh);
              }
            } catch { /* ignore */ }
          }}
        />
      </RecordPage>
    </DashboardLayout>
  );
}
